import { vinConfirmValueRecipe } from '../recipes/mobile.recipe';
import { Button } from './Button';
import { DataValue } from './DataValue';
import { Grid, Stack } from './Layout';
import { Loading } from './Spinner';
import { Text } from './Text';
import { VehicleRow, type VehicleRowProps } from './VehicleRow';

/*
 * The VIN scan flow on a phone, past the camera: a wait while the picture
 * is read, and the VIN it found to confirm. Typing one is the vehicle
 * sheet's own VIN box, which "Enter manually" and "Tap to edit" go to. The
 * app owns the request that reads the picture and which step is showing;
 * these only draw.
 */

export interface VinScanProcessingProps {
  /** What the wait is for. */
  label?: string;
}

/**
 * The wait while the picture is read and the VIN decoded.
 *
 *   {step === 'reading' && <VinScanProcessing />}
 */
export function VinScanProcessing({
  label = 'Reading the VIN…',
}: VinScanProcessingProps) {
  return <Loading label={label} title={label} fill="parent" />;
}

export interface VinConfirmProps {
  vin: string;
  /** What the VIN decodes to, when it decoded. */
  vehicle?: Pick<VehicleRowProps, 'make' | 'model' | 'year' | 'color'>;
  onConfirm: () => void;
  /** Change it by hand: "Enter manually", or a tap on the VIN itself. */
  onEnterManually: () => void;
  /** The VIN is being saved: the confirm spins. */
  saving?: boolean;
}

/**
 * The VIN the camera found, to check before it goes on the call, as the
 * Figma's Processing VIN sheet shows it: the VIN in an input's outline with
 * "Tap to edit" under it, the vehicle it decodes to, and Enter manually
 * beside Confirm. Monospaced, so a misread 8 for a B shows; a tap on it
 * goes to the typed entry with it filled in, for one wrong character.
 *
 *   <VinConfirm vin={result.vin} vehicle={decoded} onConfirm={save}
 *     onEnterManually={type} />
 */
export function VinConfirm({
  vin,
  vehicle,
  onConfirm,
  onEnterManually,
  saving = false,
}: VinConfirmProps) {
  return (
    <Stack gap={6}>
      <Stack gap={2} align="center">
        <button
          type="button"
          className={vinConfirmValueRecipe()}
          onClick={onEnterManually}
          disabled={saving}
          aria-label={`VIN ${vin}, tap to edit`}
        >
          <DataValue value={vin} format="vin" />
        </button>
        <Text variant="body" tone="muted" aria-hidden="true">
          Tap to edit
        </Text>
      </Stack>
      {vehicle ? <VehicleRow {...vehicle} /> : null}
      <Grid cols={2} gap={4}>
        <Button
          variant="secondary"
          size="xl"
          pill
          onClick={onEnterManually}
          disabled={saving}
        >
          Enter manually
        </Button>
        <Button size="xl" pill onClick={onConfirm} loading={saving}>
          Confirm
        </Button>
      </Grid>
    </Stack>
  );
}
